'use client'

import {
    deletePlanTier,
    getPlanTiers,
    PlanTier,
} from '@/lib/api/plan-tiers/plan-tiers-api'
import { useEffect, useState } from 'react'
import {
    MdAdd,
    MdDelete,
    MdEdit,
    MdLayers,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import ConfirmModal from '@/components/confirm-modal'

export default function PlanTiersPage() {
    const navigate = useNavigate()
    const [tiers, setTiers] = useState<PlanTier[]>([])
    const [loading, setLoading] = useState(true)
    const [deleteId, setDeleteId] = useState<number | null>(null)
    const [deleting, setDeleting] = useState(false)

    useEffect(() => {
        loadTiers()
    }, [])

    const loadTiers = async () => {
        setLoading(true)
        try {
            const response = await getPlanTiers()
            if (response.success) {
                setTiers(response.result || [])
            } else {
                toast.error(response.error || 'Erro ao carregar tiers')
            }
        } catch {
            toast.error('Erro ao carregar tiers')
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = async () => {
        if (!deleteId) return
        setDeleting(true)
        try {
            const response = await deletePlanTier(deleteId)
            if (response.success) {
                setTiers(prev => prev.filter(t => t.id !== deleteId))
                toast.success('Tier removido com sucesso')
            } else {
                toast.error(response.error || 'Erro ao remover tier')
            }
        } catch {
            toast.error('Erro ao remover tier')
        } finally {
            setDeleteId(null)
            setDeleting(false)
        }
    }

    if (loading && tiers.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando tiers...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-secondary via-secondary/90 to-secondary/70 flex items-center justify-center shadow-lg shadow-secondary/20">
                        <MdLayers className="text-3xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-base-content">
                            Tiers de Plano
                        </h1>
                        <p className="text-sm md:text-base text-base-content/60 mt-1">
                            {tiers.length}{' '}
                            {tiers.length === 1
                                ? 'tier cadastrado'
                                : 'tiers cadastrados'}
                        </p>
                    </div>
                </div>
                <button
                    onClick={() => navigate('/plan-tiers/new')}
                    className="btn btn-secondary gap-2 shadow-lg shadow-secondary/20"
                >
                    <MdAdd className="text-lg" />
                    Novo Tier
                </button>
            </div>

            {tiers.length === 0 ? (
                <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-2xl border border-base-300/50 shadow-lg p-16 text-center">
                    <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-secondary/10 to-secondary/5 flex items-center justify-center">
                        <MdLayers className="text-5xl text-secondary/40" />
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold text-base-content mb-3">
                        Nenhum tier cadastrado
                    </h3>
                    <p className="text-base-content/60 mb-8 max-w-md mx-auto">
                        Tiers definem os niveis hierarquicos dos planos. Modulos sao vinculados a tiers e planos herdam automaticamente.
                    </p>
                    <button
                        onClick={() => navigate('/plan-tiers/new')}
                        className="btn btn-secondary btn-lg gap-2 shadow-lg shadow-secondary/20"
                    >
                        <MdAdd className="text-xl" />
                        Criar Primeiro Tier
                    </button>
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="table table-zebra w-full">
                        <thead>
                            <tr>
                                <th>Nome</th>
                                <th className="text-center">Módulos</th>
                                <th className="text-center">Planos</th>
                                <th className="w-24">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tiers.map(tier => (
                                <tr key={tier.id} className="hover">
                                    <td>
                                        <span className="font-semibold text-base-content">
                                            {tier.name}
                                        </span>
                                    </td>
                                    <td className="text-center">
                                        <span className="badge badge-outline badge-sm">
                                            {tier._count?.modules ?? 0}
                                        </span>
                                    </td>
                                    <td className="text-center">
                                        <span className="badge badge-outline badge-sm">
                                            {tier._count?.plans ?? 0}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="flex items-center gap-1">
                                            <button
                                                type="button"
                                                className="btn btn-ghost btn-xs"
                                                onClick={() =>
                                                    navigate(
                                                        `/plan-tiers/edit/${tier.id}`,
                                                    )
                                                }
                                                title="Editar"
                                            >
                                                <MdEdit className="text-base" />
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-ghost btn-xs text-error"
                                                onClick={() =>
                                                    setDeleteId(tier.id)
                                                }
                                                title="Remover"
                                            >
                                                <MdDelete className="text-base" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <ConfirmModal
                isOpen={!!deleteId}
                onClose={() => setDeleteId(null)}
                onConfirm={handleDelete}
                title="Confirmar Exclusao"
                message="Tem certeza que deseja remover este tier? Modulos e planos vinculados serao desassociados."
                loading={deleting}
                confirmButtonText="Remover"
                confirmButtonClass="btn-error"
            />
        </div>
    )
}
